import * as Popover from '@radix-ui/react-popover'
import { RgbaColorPicker } from 'react-colorful'

export function parseColorToRgba(color) {
    if (!color || color === 'transparent') return { r: 0, g: 0, b: 0, a: 0 }
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
    if (rgbaMatch) {
        return { r: parseInt(rgbaMatch[1]), g: parseInt(rgbaMatch[2]), b: parseInt(rgbaMatch[3]), a: rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1 }
    }
    if (color.startsWith('#')) {
        const hex = color.length === 4 ? '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3] : color
        return { r: parseInt(hex.slice(1, 3), 16), g: parseInt(hex.slice(3, 5), 16), b: parseInt(hex.slice(5, 7), 16), a: 1 }
    }
    return { r: 0, g: 0, b: 0, a: 1 }
}

export function rgbaToString({ r, g, b, a }) {
    return `rgba(${r}, ${g}, ${b}, ${a})`
}

export function rgbaToHex({ r, g, b }) {
    return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')
}

export function isTransparentColor(color) {
    return parseColorToRgba(color).a === 0
}

export default function ColorPicker({ label, value, onChange, allowAlpha = true, swatchClassName = 'w-5 h-5' }) {
    const rgba = parseColorToRgba(value)
    return (
        <div className='flex items-center gap-1'>
            {label && <label className='text-[10px] text-secondary-foreground/50'>{label}</label>}
            <Popover.Root>
                <Popover.Trigger asChild>
                    <button
                        className={`${swatchClassName} rounded-sm border border-secondary-foreground/20 cursor-pointer relative overflow-hidden`}
                        style={{ background: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 8px 8px' }}
                        title={value || 'transparent'}
                    >
                        <span className='absolute inset-0 rounded-sm' style={{ background: value || 'transparent' }} />
                    </button>
                </Popover.Trigger>
                <Popover.Portal>
                    <Popover.Content sideOffset={6} className='z-50 rounded-md border bg-popover p-3 shadow-md flex flex-col gap-2.5' onClick={(e) => e.stopPropagation()}>
                        <RgbaColorPicker
                            color={rgba}
                            onChange={(c) => onChange(rgbaToString(allowAlpha ? c : { ...c, a: 1 }))}
                        />
                        <div className='flex items-center gap-1.5'>
                            <input
                                type='text'
                                className='flex-1 text-[10px] bg-secondary border rounded-sm px-1.5 py-1 text-secondary-foreground outline-none font-mono'
                                value={rgbaToHex(rgba)}
                                onChange={(e) => {
                                    const v = e.target.value
                                    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                                        const parsed = parseColorToRgba(v)
                                        onChange(rgbaToString({ ...parsed, a: allowAlpha ? rgba.a : 1 }))
                                    }
                                }}
                            />
                            {allowAlpha && (
                                <>
                                    <span className='text-[10px] text-secondary-foreground/50'>{Math.round(rgba.a * 100)}%</span>
                                    {rgba.a > 0 && (
                                        <button
                                            className='text-[10px] text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer'
                                            onClick={() => onChange('transparent')}
                                            title='Clear / transparent'
                                        >
                                            Clear
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    )
}
